/**
 * Decide which Home copy to show when a browser cache and the server disagree.
 * Button identity, actions, and positions are the shared record.
 * Shortcuts chrome is device-local and is not part of that record.
 */

const SHARED_ICON_FIELDS = [
  'id',
  'position',
  'label',
  'actionType',
  'flowId',
  'actionId',
  'pagingZoneId',
  'sipPagingZoneId',
  'iconType',
  'iconKey',
  'iconText',
  'iconImageAssetId',
];

const SHARED_LAYOUT_FIELDS = [
  'theme',
  'gridColumns',
  'gridRows',
  'iconSize',
  'gap',
  'showGrid',
  'preset',
  'seededFromWorkspaceId',
  'seededFromVersionId',
];

export function layoutSaveContext({ uid = '', tenantId = '', workspaceId = '' } = {}) {
  return `${uid}|${tenantId}|${workspaceId}`;
}

export function revisionOf(layout) {
  const value = Number(layout?.revision);
  return Number.isInteger(value) && value >= 0 ? value : null;
}

export function personalHomeRecord(layout) {
  if (!layout || typeof layout !== 'object') return null;
  const icons = (Array.isArray(layout.icons) ? layout.icons : []).map((icon) => {
    const row = {};
    SHARED_ICON_FIELDS.forEach((key) => {
      if (icon?.[key] != null && icon[key] !== '') row[key] = icon[key];
    });
    row.position = Number.isInteger(icon?.position) ? icon.position : 0;
    return row;
  }).sort((a, b) => String(a.id || '').localeCompare(String(b.id || '')) || a.position - b.position);
  const record = { icons };
  SHARED_LAYOUT_FIELDS.forEach((key) => {
    if (layout[key] != null && layout[key] !== '') record[key] = layout[key];
  });
  return record;
}

export function sameHomeRecord(left, right) {
  return JSON.stringify(personalHomeRecord(left)) === JSON.stringify(personalHomeRecord(right));
}

/**
 * @returns {{ action: 'use-server'|'keep-local'|'conflict'|'recover', layout: object|null, serverLayout: object|null, localLayout: object|null }}
 */
export function reconcileHomePreferences({ server, local, pending = false } = {}) {
  const serverLayout = server && typeof server === 'object' ? server : null;
  const localLayout = local && typeof local === 'object' ? local : null;
  const stored = Boolean(serverLayout?.stored);
  const serverRevision = revisionOf(serverLayout);
  const localRevision = revisionOf(localLayout);

  if (!localLayout) {
    return { action: 'use-server', layout: serverLayout, serverLayout, localLayout: null };
  }
  if (!stored) {
    const localIcons = Array.isArray(localLayout.icons) ? localLayout.icons : [];
    if (!pending && localIcons.length === 0) {
      return { action: 'use-server', layout: serverLayout, serverLayout, localLayout };
    }
    return { action: 'keep-local', layout: localLayout, serverLayout, localLayout };
  }
  if (sameHomeRecord(serverLayout, localLayout)) {
    return { action: 'use-server', layout: serverLayout, serverLayout, localLayout };
  }
  if (serverRevision != null && localRevision != null && serverRevision > localRevision) {
    if (pending) return { action: 'conflict', layout: localLayout, serverLayout, localLayout };
    return { action: 'use-server', layout: serverLayout, serverLayout, localLayout };
  }
  if (pending) return { action: 'conflict', layout: localLayout, serverLayout, localLayout };
  return { action: 'keep-local', layout: localLayout, serverLayout, localLayout };
}

export function remoteHomeOnFocus({ serverRevision, baseRevision, dirty, sameContent }) {
  if (sameContent || serverRevision == null || baseRevision == null) return 'current';
  if (serverRevision === baseRevision) return 'current';
  if (serverRevision > baseRevision) return dirty ? 'conflict' : 'reload';
  return 'current';
}

export function interpretLayoutSaveError(error) {
  const message = String(error?.message || '');
  const match = message.match(/^API Error \((\d+)\): ([\s\S]*)$/);
  if (!match) return { phase: 'error', message: message || 'Could not save Home' };
  let payload = null;
  try {
    payload = JSON.parse(match[2]);
  } catch {
    payload = null;
  }
  if (match[1] === '409' || payload?.error === 'layout_conflict') {
    return {
      phase: 'conflict',
      message: payload?.message || 'Home was updated on another device.',
      current: payload?.data || null,
    };
  }
  return { phase: 'error', message: payload?.message || payload?.error || 'Could not save Home' };
}

export function savePhaseAfterConfirmation(error) {
  if (!error) return 'saved';
  return interpretLayoutSaveError(error).phase;
}
